import { useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { signIn, resetPasswordForEmail } from '../lib/auth'
import { useAuth } from '../context/AuthContext'
import { showToast } from '../components/Toast'
import PromoBanner from '../components/PromoBanner'

export default function Login() {
  const navigate = useNavigate()
  const [params] = useSearchParams()
  const { refresh } = useAuth()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState('')
  // ?forgot=1 opens the modal straight away. The "already have an account"
  // screen on signup links here — /reset-password is where a recovery LINK
  // lands, not where you ask for one, so it would have been a dead end.
  const [resetOpen, setResetOpen] = useState(params.get('forgot') === '1')
  const [resetEmail, setResetEmail] = useState('')
  const [resetSubmitting, setResetSubmitting] = useState(false)

  async function handleSubmit(e) {
    e.preventDefault()
    setError('')
    setSubmitting(true)
    try {
      await signIn({ email, password })
      await refresh()
      navigate('/order')
    } catch (err) {
      setError(err.message || 'Login failed. Check your email and password.')
      setSubmitting(false)
    }
  }

  async function handleReset(e) {
    e.preventDefault()
    if (!resetEmail) return
    setResetSubmitting(true)
    try {
      await resetPasswordForEmail(resetEmail)
      showToast('Reset link sent — check your email')
      setResetOpen(false)
      setResetEmail('')
    } catch (err) {
      showToast(err.message || 'Could not send reset email')
    } finally {
      setResetSubmitting(false)
    }
  }

  return (
    <div className="mx-auto flex min-h-screen max-w-md flex-col items-center justify-center px-6 py-10">
      <div className="mb-8 flex flex-col items-center text-center">
        <img
          src="/logo-ghost.png"
          alt=""
          aria-hidden="true"
          width="80"
          height="80"
          className="mb-4 h-20 w-20 rounded-[20px] shadow-[0_10px_30px_rgba(11,32,56,0.25)]"
        />
        <img
          src="/logo-wordmark.png"
          alt="Ghost Laundry"
          height="28"
          className="h-7 w-auto"
        />
        <p className="mt-2 text-[13px] text-ink-2">Book a pickup in 30 seconds</p>
      </div>

      <PromoBanner />

      <div className="w-full rounded-2xl border border-line bg-white p-5 shadow-sm">
        <div className="mb-5 grid grid-cols-2 gap-1 rounded-lg bg-[#F3F4F6] p-1">
          <button
            type="button"
            className="rounded-md bg-white py-2 text-[13px] font-extrabold text-navy shadow-sm"
          >
            Log In
          </button>
          <button
            type="button"
            onClick={() => navigate('/entry')}
            className="rounded-md py-2 text-[13px] font-bold text-ink-2"
          >
            Sign Up
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-3">
          <div>
            <label className="field-label">Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="email"
              placeholder="you@example.com"
              className="field-input"
              required
            />
          </div>
          <div>
            <label className="field-label">Password</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
              placeholder="••••••••"
              className="field-input"
              required
              minLength={6}
            />
            <div className="mt-1 text-right">
              <button
                type="button"
                onClick={() => {
                  setResetEmail(email)
                  setResetOpen(true)
                }}
                className="text-[11px] font-bold text-aqua"
              >
                Forgot password?
              </button>
            </div>
          </div>
          {error && (
            <p className="rounded-lg bg-red/10 px-3 py-2 text-[12px] font-bold text-red">
              {error}
            </p>
          )}
          <button
            type="submit"
            disabled={submitting}
            className={`btn-navy mt-2 ${submitting ? 'opacity-60' : ''}`}
          >
            {submitting ? 'Logging in…' : 'Log In'}
          </button>
        </form>

        <p className="mt-5 text-center text-[12px] text-ink-2">
          Don&apos;t have an account?{' '}
          <Link to="/entry" className="font-extrabold text-aqua">Sign Up</Link>
        </p>
      </div>

      {/* Forgot-password modal */}
      {resetOpen && (
        <div
          className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 px-4 pb-4 pt-6 sm:items-center sm:pb-6"
          onClick={() => setResetOpen(false)}
          role="dialog"
          aria-modal="true"
        >
          <div
            className="w-full max-w-sm rounded-2xl bg-white p-5 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mb-2 flex items-center justify-between">
              <h2 className="font-bricolage text-[15px] font-extrabold text-navy">
                Reset password
              </h2>
              <button
                type="button"
                onClick={() => setResetOpen(false)}
                aria-label="Close"
                className="text-ink-3 hover:text-ink"
              >
                ✕
              </button>
            </div>
            <p className="mb-3 text-[12px] text-ink-2">
              Enter your email — we&apos;ll send a secure reset link.
            </p>
            <form onSubmit={handleReset} className="space-y-3">
              <input
                type="email"
                value={resetEmail}
                onChange={(e) => setResetEmail(e.target.value)}
                placeholder="you@example.com"
                className="field-input"
                required
              />
              <button
                type="submit"
                disabled={resetSubmitting}
                className={`btn-navy ${resetSubmitting ? 'opacity-60' : ''}`}
              >
                {resetSubmitting ? 'Sending…' : 'Send reset link'}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}
